import { projectId, publicAnonKey } from '/utils/supabase/info';

const ENDPOINT = `https://${projectId}.supabase.co/functions/v1/make-server-43ef46c1/client-errors`;

const seen = new Set<string>();
const MAX_DISTINCT = 25;

export interface ClientErrorPayload {
  source: 'error' | 'unhandledrejection' | 'react' | 'handled' | 'api';
  message: string;
  stack?: string;
  componentStack?: string;
}

export function reportClientError(payload: ClientErrorPayload): void {
  try {
    const key = `${payload.source}|${payload.message}`;
    if (seen.has(key) || seen.size >= MAX_DISTINCT) return;
    seen.add(key);

    const clip = (v: string | undefined, max: number) => (v && v.length > max ? v.slice(0, max) : v);
    const token = (publicAnonKey || '').replace(/[\r\n\t]/g, '').trim();
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        source: payload.source,
        message: clip(payload.message, 1000),
        stack: clip(payload.stack, 4000),
        component_stack: clip(payload.componentStack, 4000),
        url: typeof location !== 'undefined' ? location.href : '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* no-op */
  }
}

// A user-facing error toast (`toast.error(...)`) that the app handled but the
// user still saw. Deduplicated and rate-limited by reportClientError.
export function reportHandledError(message: string): void {
  if (!message) return;
  reportClientError({ source: 'handled', message });
}

// A failed API request: network unreachable (status 0) or a 5xx from the
// server. 4xx are the caller's problem, not an incident, so they're filtered
// out here — the API client can hand every failure over unconditionally.
export function reportApiError(status: number, url: string, detail?: string): void {
  if (status !== 0 && status < 500) return;
  let path = url;
  try {
    const u = new URL(url, typeof location !== 'undefined' ? location.origin : 'http://localhost');
    path = u.pathname;
  } catch {
    /* keep the raw url */
  }
  const label = status === 0 ? 'network error' : `HTTP ${status}`;
  const detailText = detail ? ` — ${String(detail).slice(0, 200)}` : '';
  reportClientError({ source: 'api', message: `API ${label}: ${path}${detailText}` });
}

let toastsInstrumented = false;

// Wrap sonner's toast.error so every error toast is also reported as a
// 'handled' client error. The wrap is best-effort: telemetry never throws and
// never keeps the original toast from showing.
export function instrumentToasts(): void {
  if (toastsInstrumented || typeof window === 'undefined') return;
  toastsInstrumented = true;
  import('sonner').then(({ toast }) => {
    const original = toast.error.bind(toast);
    toast.error = ((message: unknown, data?: { description?: unknown }) => {
      try {
        const text = typeof message === 'string'
          ? message
          : (typeof data?.description === 'string' ? data.description : '');
        if (text) reportHandledError(text);
      } catch {
        /* never let telemetry break a toast */
      }
      return (original as (m: unknown, d?: unknown) => string | number)(message, data);
    }) as typeof toast.error;
  }).catch(() => {
    toastsInstrumented = false; // import failed — let a later call retry
  });
}

export function installGlobalErrorReporting(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (e) => {
    if (e.message === 'Script error.' && !e.error && !e.filename) return;
    reportClientError({
      source: 'error',
      message: e.message || 'Uncaught error',
      stack: (e.error && e.error.stack) || undefined,
    });
  });
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason: any = e.reason;
    reportClientError({
      source: 'unhandledrejection',
      message: (reason && (reason.message || String(reason))) || 'Unhandled rejection',
      stack: reason && reason.stack ? reason.stack : undefined,
    });
  });
  instrumentToasts();
}
